import { ReviewCard } from "@/components/company/reviews/review-card"
import type { Signal } from "@/constants/signal/signal"
import {
  hasParaphrasedReviewText,
  type ReviewWithData,
} from "@/helpers/company/review-utils"

type ReviewSampleProps = {
  reviews: ReviewWithData[]
  signals?: readonly Signal[]
  layout?: "grid" | "stacked"
  shouldHideTags?: boolean
}

// Prefer positive signal for badge display — matches the proportional review selection
// which favors showing positive reviews for high-sentiment phases.
function pickSignalForReview(
  review: ReviewWithData,
  signals: readonly Signal[]
): Signal | undefined {
  const reviewSignals = review.signals
  if (!reviewSignals) return undefined

  for (const signal of signals) {
    if (reviewSignals[signal]?.label === "positive") return signal
  }
  for (const signal of signals) {
    if (reviewSignals[signal]?.label === "negative") return signal
  }
  return undefined
}
export function ReviewSample({
  reviews,
  signals,
  layout = "stacked",
  shouldHideTags = false,
}: ReviewSampleProps) {
  const displayReviews = reviews.filter(hasParaphrasedReviewText).slice(0, 3)

  if (displayReviews.length === 0) {
    return (
      <p className="text-muted-foreground text-center">No reviews available</p>
    )
  }

  if (layout === "stacked") {
    return (
      <div className="divide-border divide-y">
        {displayReviews.map((review, index) => {
          const signal = signals
            ? pickSignalForReview(review, signals)
            : undefined
          return (
            <div key={review.id} className="py-6 first:pt-0 last:pb-0">
              <ReviewCard
                review={review}
                signal={signal}
                number={index + 1}
                shouldHideTags={shouldHideTags}
              />
            </div>
          )
        })}
      </div>
    )
  }

  // Sort by text length so the two shortest are first (top row) and longest is last (full width)
  const sorted = [...displayReviews].sort(
    (a, b) =>
      (a.paraphrasedText?.length ?? 0) - (b.paraphrasedText?.length ?? 0)
  )
  const shortReviews = sorted.slice(0, 2)
  const longReview = sorted[2]

  return (
    <div className="divide-border divide-y">
      <div className="grid grid-cols-1 gap-6 pb-6 md:grid-cols-2">
        {shortReviews.map((review) => {
          const signal = signals
            ? pickSignalForReview(review, signals)
            : undefined
          return (
            <ReviewCard
              key={review.id}
              review={review}
              signal={signal}
              shouldHideTags={shouldHideTags}
            />
          )
        })}
      </div>
      {longReview && (
        <div className="py-6 last:pb-0">
          <ReviewCard
            review={longReview}
            signal={
              signals ? pickSignalForReview(longReview, signals) : undefined
            }
            shouldHideTags={shouldHideTags}
          />
        </div>
      )}
    </div>
  )
}
